import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import {
  ArrowUpRight, BarChart3, Box, CheckCircle2, CircleDot, Cog, 
  Package, ShieldCheck, Star, TrendingUp, Menu, X,
  Car, Plane, Factory, Mountain, Zap, Utensils, ArrowLeft, ArrowRight
} from 'lucide-react';

import heroBg from '../assets/images/regenerated_image_1778498558401.jpg';
import product1Img from '../assets/images/regenerated_image_1778485893446.webp';
import product2Img from '../assets/images/regenerated_image_1778486102604.jpg';
import product3Img from '../assets/images/regenerated_image_1778486103913.webp';
import heroRightImg from '../assets/images/regenerated_image_1778486104815.jpg';
import schaefflerBg from '../assets/images/regenerated_image_1778494496893.png';
import inaBg from '../assets/images/regenerated_image_1778494497814.png';
import fagBg from '../assets/images/regenerated_image_1778494499556.webp';
import precisionBg from '../assets/images/regenerated_image_1778495542308.jpg';
import mountedUnitsBg from '../assets/images/regenerated_image_1778495544368.jpg';
import additionalHeroImg from '../assets/images/regenerated_image_1778674971295.jpg';
import carouselImg1 from '../assets/images/regenerated_image_1778484525987.jpg';
import carouselImg2 from '../assets/images/regenerated_image_1778495547337.jpg';
import carouselImg3 from '../assets/images/regenerated_image_1778499097385.webp';
import carouselImg4 from '../assets/images/regenerated_image_1779110909558.png';
import jkFennerLogo from '../assets/images/regenerated_image_1779102332709.png';
import castrolLogo from '../assets/images/regenerated_image_1779109797018.png';
import schaefflerLogo from '../assets/images/regenerated_image_1779108751261.jpg';
import gsCaltexLogo from '../assets/images/regenerated_image_1779109450497.png';
import mastaLogo from '../assets/images/regenerated_image_1779109598201.png';

import { productRanges } from '../data/productRanges';
import { Link } from 'react-router-dom';

const FadeIn = ({ children, delay = 0, className = "" }: { children: React.ReactNode, delay?: number, className?: string, key?: React.Key }) => (
  <div className={className}>
    {children}
  </div>
);

const heroCarouselImages = [
  heroRightImg,
  carouselImg1,
  carouselImg2,
  carouselImg3,
  carouselImg4
];

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroCarouselImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="pt-20 px-4 md:px-12 max-w-7xl mx-auto flex flex-col items-center">
      <div className="flex flex-col lg:flex-row items-stretch w-full border-b border-slate-200 bg-white">
        {/* Left Column */}
        <div className="w-full lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center relative overflow-hidden group">
          <div className="absolute inset-0 z-0">
             <img src={heroBg} alt="Industrial Background" className="w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" loading="eager" />
             <div className="absolute inset-0 bg-white/30 group-hover:bg-white/20 transition-colors duration-700"></div>
          </div>
          <div className="relative z-10">
            <FadeIn>
              <h2 className="text-xs font-bold text-red-600 uppercase tracking-[0.3em] mb-4">Precision Distribution</h2>
              <h1 className="text-5xl font-bold text-black leading-tight mb-6 transition-colors duration-500 text-left">
                India’s Trusted Partner for <br/><span className="group-hover:text-red-600 transition-colors duration-500">High-Performance Industrial Solutions</span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.2} className="flex flex-col sm:flex-row items-center gap-4">
              <Link 
                to="/catalog"
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-600/20 active:scale-95 text-center"
              >
                Order Now
              </Link>
              <motion.div
                animate={{
                  scale: [1, 1.02, 1],
                  boxShadow: ["0px 0px 0px rgba(220, 38, 38, 0)", "0px 0px 12px rgba(220, 38, 38, 0.15)", "0px 0px 0px rgba(220, 38, 38, 0)"]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-full sm:w-auto"
              >
                <Link 
                  to="/contact"
                  className="w-full sm:w-auto px-6 py-3 bg-red-50 text-red-700 border border-red-100 shadow-sm shadow-red-100/50 text-xs font-bold uppercase tracking-widest hover:bg-red-100 hover:border-red-200 hover:text-red-800 transition-all duration-300 inline-flex items-center justify-center gap-2 text-center group ring-1 ring-red-500/10"
                >
                  Contact Us
                  <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </motion.div>
            </FadeIn>
          </div>
        </div>

        {/* Right Column */}
        <FadeIn delay={0.3} className="w-full lg:w-1/2 bg-slate-100 p-8 lg:p-12 border-l border-slate-200 relative overflow-hidden">
           <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
             <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(220,38,38,0.1)_1px,transparent_1px)] bg-[length:40px_40px]"></div>
             <div className="absolute top-0 right-0 w-full h-full opacity-[0.03] pointer-events-none" style={{ backgroundImage: "linear-gradient(90deg, #dc2626 1px, transparent 1px), linear-gradient(#dc2626 1px, transparent 1px)", backgroundSize: "100px 100px" }}></div>

             <motion.div 
               animate={{ rotate: 360 }}
               transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
               className="absolute -top-24 -right-24 w-96 h-96 text-red-500/5"
             >
               <svg viewBox="0 0 24 24" fill="currentColor">
                 <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-8L10 5h4l-2-3zm0 20l2-3h-4l2 3zm-9-11l3-2v4l-3-2zm18 0l-3 2v-4l3 2zm-12.243-7.757l2.121 2.122-2.828 2.828-2.122-2.122 2.829-2.828zm11.314 11.314l-2.122-2.122 2.828-2.828 2.122 2.122-2.828 2.828zm-11.314 0l-2.122-2.122-2.828 2.828 2.122 2.122 2.828-2.828zm11.314-11.314l2.122 2.122-2.828 2.828-2.122-2.122 2.828-2.828z" />
               </svg>
             </motion.div>

             <div 
               className="absolute top-[20%] left-[10%] w-[300px] h-[300px] border border-red-500/20 rounded-full"
             />
             <div 
               className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-gradient-to-tr from-red-500/10 to-transparent blur-[40px] md:blur-3xl"
             />
             
             <div className="absolute top-12 left-12 w-24 h-px bg-red-600/20"></div>
             <div className="absolute top-12 left-12 w-px h-24 bg-red-600/20"></div>
             <div className="absolute bottom-12 right-12 w-24 h-px bg-red-600/20"></div>
             <div className="absolute bottom-12 right-12 w-px h-24 bg-red-600/20"></div>
           </div>

           <motion.div 
             animate={{ y: [0, -10, 0] }}
             transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
             className="relative w-full h-full min-h-[400px] border border-slate-200 bg-white overflow-hidden group z-10 shadow-2xl"
           >
             {heroCarouselImages.map((img, index) => (
                <img 
                  key={index}
                  src={img} 
                  alt={`Manufacturing showcase ${index + 1}`} 
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${
                    index === currentImageIndex ? "opacity-100 scale-100" : "opacity-0 scale-95"
                  }`} 
                  loading={index === 0 ? "eager" : "lazy"}
                />
             ))}
             <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-transparent transition-colors duration-500"></div>
             <div className="absolute bottom-8 right-8 text-right bg-white/90 backdrop-blur p-4 border border-slate-100 shadow-sm z-10 transition-transform duration-500 group-hover:-translate-y-1">
                <p className="text-4xl font-bold text-slate-800">1500+</p>
                <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">Esteemed Clients</p>
             </div>
           </motion.div>
        </FadeIn>
      </div>

      <FadeIn delay={0.4} className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-l border-r border-slate-200 bg-white">
          <div className="p-8 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col justify-center items-center text-center">
            <div className="flex text-yellow-400 mb-2">
              {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            <span className="text-slate-900 font-bold mb-1">5.0</span>
            <span className="text-slate-500 text-xs uppercase tracking-widest">from 1500+ clients</span>
          </div>
          
          <div className="p-8 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col justify-center text-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-red-50 transform scale-0 group-hover:scale-100 transition-transform duration-500 rounded-full mix-blend-multiply opacity-50"></div>
            <h3 className="text-4xl font-bold text-slate-900 mb-2 z-10">10k+</h3>
            <p className="text-xs text-slate-500 uppercase tracking-widest z-10">Products In Stock</p>
          </div>
          
          <div className="p-8 justify-center text-center">
            <h4 className="text-4xl font-bold text-slate-900 mb-2">20+</h4>
            <p className="text-xs text-slate-500 uppercase tracking-widest">Years of Service</p>
          </div>
        </div>
      </FadeIn>
    </section>
  );
};

const Services = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const scrollAmount = window.innerWidth * 0.8;
      
      let scrollTo: number;
      if (direction === 'left') {
        if (scrollLeft <= 10) {
          scrollTo = scrollWidth - clientWidth;
        } else {
          scrollTo = scrollLeft - scrollAmount;
        }
      } else {
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollTo = 0;
        } else {
          scrollTo = scrollLeft + scrollAmount;
        }
      }
      
      scrollRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="services" className="py-24 px-4 md:px-12 max-w-7xl mx-auto">
      <div className="bg-slate-100 p-8 md:p-12 border border-slate-200 relative group/services">
        <FadeIn className="mb-12 flex flex-col items-center text-center">
          <h3 className="text-2xl font-semibold text-slate-900">Comprehensive Product Range</h3>
          <div className="w-12 h-1 bg-red-600 mt-2"></div>
          <p className="text-slate-500 mt-4 max-w-2xl px-4 font-bold">Discover our extensive inventory of premium bearings, engineered to meet the exact demands of your industry and keep your operations moving.</p>
        </FadeIn>

        <div className="relative">
          {/* Mobile Arrows */}
          <button 
            onClick={() => scroll('left')}
            className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm border border-slate-200 p-2 rounded-full shadow-lg text-slate-900 active:scale-90 transition-all hover:bg-white"
          >
            <ArrowLeft size={20} />
          </button>
          <button 
            onClick={() => scroll('right')}
            className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm border border-slate-200 p-2 rounded-full shadow-lg text-slate-900 active:scale-90 transition-all hover:bg-white"
          >
            <ArrowRight size={20} />
          </button>

          <div 
            ref={scrollRef}
            className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-x-auto md:overflow-x-visible pb-8 md:pb-0 snap-x scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" 
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {productRanges.map((item, i) => (
              <div key={i} className="flex-shrink-0 w-[85vw] md:w-auto snap-center">
                <FadeIn delay={i * 0.05} className="h-full">
                  <Link to={`/products/${item.id}`} className="block h-full">
                    <div className="bg-white border border-slate-200 hover:border-red-600 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group cursor-pointer h-full flex flex-col overflow-hidden rounded-xl md:rounded-none">
                      <div className="h-48 relative overflow-hidden">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-transparent transition-colors duration-500"></div>
                      </div>
                      <div className="p-8 pt-6 flex flex-col flex-grow items-center text-center">
                        <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed mb-auto font-bold italic">{item.desc}</p>
                        <div className="mt-6 w-full flex justify-end">
                          <ArrowUpRight className="text-slate-300 group-hover:text-red-600 transition-colors" size={20}/>
                        </div>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const industries = [
  {
    title: "Automotive",
    desc: "Precision bearings for transmissions, wheel hubs, and engine components ensuring long-term reliability on the road.",
    icon: <Car size={24} />,
    color: "bg-blue-50 text-blue-600"
  },
  {
    title: "Aerospace",
    desc: "Flight-critical solutions designed for extreme temperatures and high-speed requirements in turbine and navigation systems.",
    icon: <Plane size={24} />,
    color: "bg-indigo-50 text-indigo-600"
  },
  {
    title: "Steel & Metal",
    desc: "Heavy-duty, heat-resistant bearings optimized for the harsh environments of rolling mills and casting lines.",
    icon: <Factory size={24} />,
    color: "bg-orange-50 text-orange-600"
  },
  {
    title: "Mining & Cement",
    desc: "Rugged components built to withstand high vibrations and abrasive conditions in crushers and conveyor systems.",
    icon: <Mountain size={24} />,
    color: "bg-stone-50 text-stone-600"
  },
  {
    title: "Power & Energy",
    desc: "Efficient solutions for wind turbines, thermal plants, and renewable energy infrastructure to maximize uptime.",
    icon: <Zap size={24} />,
    color: "bg-yellow-50 text-yellow-700"
  },
  {
    title: "Food & Beverage",
    desc: "Corrosion-resistant, lubrication-free bearings meeting strict hygienic standards for processing and packaging.",
    icon: <Utensils size={24} />,
    color: "bg-emerald-50 text-emerald-600"
  }
];

const IndustriesSection = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      if (scrollLeft <= 10) {
        scrollRef.current.scrollTo({ left: scrollWidth, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
      }
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      if (scrollLeft + clientWidth >= scrollWidth - 10) {
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
      }
    }
  };

  return (
    <section id="industries" className="py-32 px-4 md:px-12 max-w-7xl mx-auto border-t border-slate-200 relative overflow-hidden bg-white">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-30">
        <motion.div 
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-24 -left-24 w-96 h-96 border border-red-100 rounded-full"
        />
        <div className="absolute top-0 right-0 w-full h-full opacity-[0.02]" style={{ backgroundImage: "radial-gradient(#dc2626 0.5px, transparent 0.5px)", backgroundSize: "30px 30px" }}></div>
      </div>

      <div className="relative z-10">
        <div className="flex flex-col lg:flex-row justify-between items-end mb-20 gap-6">
          <FadeIn className="max-w-2xl">
            <h2 className="text-xs font-bold text-red-600 uppercase tracking-[0.3em] mb-4">Vertical Excellence</h2>
            <h3 className="text-4xl font-light text-slate-900 mb-6">Industries <span className="font-bold">We Serve</span></h3>
            <p className="text-slate-500 text-lg leading-relaxed">
              Onkar Bearing provides specialized technical support with a commitment to uncompromised quality, rigorous assurance, and proven reliability across all industrial sectors.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <Link to="/contact" className="group flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-900 border-b-2 border-slate-900 pb-1 hover:text-red-600 hover:border-red-600 transition-colors">
              Get Industry Support <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </FadeIn>
        </div>

        <div className="relative group flex items-center">
          <button onClick={scrollLeft} className="z-10 absolute left-0 md:-left-6 w-12 h-12 bg-white border border-slate-200 text-slate-900 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors shadow-lg disabled:opacity-50">
            <ArrowLeft size={20} />
          </button>

          <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-8 pt-4 scrollbar-hide snap-x px-4 w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {industries.map((industry, i) => (
              <FadeIn key={i} delay={i * 0.05} className="w-[350px] md:w-[450px] h-full flex-shrink-0 snap-center">
                <motion.div 
                  whileHover={{ y: -10 }}
                  className="h-full flex flex-col items-center text-center gap-6 group/card p-8 bg-white border border-slate-100 shadow-md hover:border-red-100 hover:shadow-2xl hover:shadow-red-500/10 transition-all duration-500 rounded-3xl"
                >
                  <div className={`w-16 h-16 shrink-0 rounded-full flex items-center justify-center transition-all duration-300 group-hover/card:scale-110 group-hover/card:shadow-lg ${industry.color}`}>
                    {industry.icon}
                  </div>
                  <div className="flex-grow flex flex-col items-center">
                    <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover/card:text-red-600 transition-colors">{industry.title}</h4>
                    <div className="w-8 h-1 bg-red-600/0 group-hover/card:bg-red-600 mb-4 transition-colors duration-500 rounded-full"></div>
                    <p className="text-slate-500 text-sm font-bold italic leading-relaxed transition-all duration-500">{industry.desc}</p>
                  </div>
                </motion.div>
              </FadeIn>
            ))}
          </div>

          <button onClick={scrollRight} className="z-10 absolute right-0 md:-right-6 w-12 h-12 bg-white border border-slate-200 text-slate-900 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors shadow-lg disabled:opacity-50">
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

const BrandsAndIntegrations = () => {
  const brands = [
    { name: "Masta", sub: "BEARING HOUSING", src: mastaLogo },
    { name: "JK Fenner", sub: "", src: jkFennerLogo },
    { name: "Castrol", sub: "", src: castrolLogo },
    { name: "GS Caltex", sub: "", src: gsCaltexLogo },
    { name: "Schaeffler", sub: "", src: schaefflerLogo }
  ];

  const duplicatedBrands = [...brands, ...brands, ...brands, ...brands];

  return (
  <section className="py-24 px-4 md:px-12 max-w-7xl mx-auto relative mt-12 mb-24">
    <div className="flex flex-col gap-16 bg-white pt-12 md:pt-16 pb-8 md:pb-12 rounded-3xl shadow-xl border border-slate-200 relative overflow-hidden">
      <div className="absolute top-10 right-10 opacity-[0.02] pointer-events-none">
         <Cog size={200} />
      </div>
      <div className="flex flex-col lg:flex-row items-center gap-16 px-8 md:px-12 lg:px-16 relative z-10">
        <FadeIn className="w-full lg:w-1/2">
          <span className="text-red-500 font-bold uppercase tracking-widest text-sm block mb-2">Brands</span>
          <h3 className="text-4xl font-bold text-slate-900 mb-6">We are authorized distributor for Schaeffler India</h3>
          <div className="w-12 h-1 bg-red-600 mb-6"></div>
          <p className="text-slate-500 mb-6 text-lg leading-relaxed">
            We are proud to be an authorized distributor for <strong className="text-slate-900">Schaeffler</strong>, bringing world-class bearing solutions to the Indian market.
          </p>
        </FadeIn>
        
        <FadeIn className="w-full lg:w-1/2" delay={0.2}>
           <div className="flex flex-col gap-4">
             {/* Schaeffler Representation */}
             <div className="relative h-56 border border-slate-200 overflow-hidden flex items-center justify-center group bg-slate-900 rounded-t-xl">
               <img 
                 src={schaefflerBg} 
                 alt="Bearings background" 
                 className="absolute inset-0 w-full h-full object-cover opacity-100 group-hover:scale-110 transition-transform duration-700" 
                 loading="lazy"
               />
               <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
             </div>
             
            <div className="relative h-32 w-full mx-auto border border-slate-200 flex items-center justify-center overflow-hidden group rounded-xl bg-white shadow-sm mt-4">
              <img 
                src={inaBg} 
                alt="Industrial part" 
                className="w-full h-full object-contain p-1 opacity-100 group-hover:scale-110 transition-transform duration-700" 
                loading="lazy"
              />
            </div>
           </div>
        </FadeIn>
      </div>

      <div className="w-full bg-slate-900 text-white rounded-b-3xl relative z-10 pt-12 pb-16 overflow-hidden">
          <div className="text-center mb-8 relative z-10">
            <h4 className="text-xl font-bold text-white tracking-wide">Other Brands We Serve</h4>
            <div className="w-12 h-1 bg-red-600 mx-auto mt-4"></div>
          </div>
          <div className="relative w-full mx-auto overflow-hidden group">
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none"></div>

            <motion.div 
              className="flex gap-6 w-max py-4"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ ease: "linear", duration: 30, repeat: Infinity }}
            >
              {duplicatedBrands.map((brand, i) => (
                <div key={i} className="w-[200px] flex-shrink-0 bg-slate-800 rounded-lg shadow-sm border border-slate-700 flex items-center justify-center h-32 hover:shadow-md hover:-translate-y-1 transition-all overflow-hidden relative">
                  <img src={brand.src} alt={brand.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </motion.div>
          </div>
      </div>
    </div>
  </section>
  );
};

const HomePage = () => {
  return (
    <>
      <Hero />
      <Services />
      <IndustriesSection />
      <BrandsAndIntegrations />
    </>
  );
};

export default HomePage;
